import { Box, Button, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../app/hooks";
import config from "../../config/config";
import { CONFIRMATION_DELAY } from "../../constants/const";
import { Messages } from "../../constants/messages";
import { dispatchTxAction } from "../../helper/dispatchTxAction";
import useCurrentTime from "../../hook/useCurrentTime";
import useGameBankBalance from "../../hook/useGameBankBalance";
import useWallet from "../../hook/useWallet";
import {
  approveGameCashAction,
  buyOutAction,
  getJailedUntilAction,
} from "../../reducers/game.slice";
import { convertDateTime, toastSuccess, toUSDFormat } from "../../utils/utils";

import useStyles from "./index.styles";
import { BribePerMin, CrimeFailedReason } from "../../constants/jail";
import useJailSSE from "./hooks/useJailSSE";
import { getGameBankAllowance } from "../../helper/contractFunctions/MafiaGameBank";

const InJailPage = () => {
  const { classes } = useStyles();
  const dispatch = useAppDispatch();
  const currentTime = useCurrentTime();
  const { account } = useWallet();
  const { jailedUntil, myCrimeType } = useAppSelector((state) => state.game);
  const { signMsg, signature } = useAppSelector((state) => state.auth);
  const { updateBalance } = useGameBankBalance(account, signMsg, signature);

  useJailSSE();

  // Add this function to calculate bribe amount
  const calculateBribeAmount = useMemo(() => {
    const jailTime = Math.max(0, jailedUntil - currentTime);
    // Convert to BigNumber for precise calculation
    const timeInSeconds = jailTime;
    const price = (timeInSeconds * BribePerMin) / 60;

    // Convert to readable format
    return price;
  }, [jailedUntil, currentTime]);

  const handleBuyOut = async () => {
    const allowance = await getGameBankAllowance(account, config.jailAddress);

    const buyOut = () => {
      dispatchTxAction(
        dispatch,
        buyOutAction({ address: account, account }),
        () => {
          toastSuccess(Messages.JAIL.BUY_OUT.SUCCESS);
          updateBalance(account, signMsg, signature);
          dispatch(getJailedUntilAction(account));
        },
        CONFIRMATION_DELAY
      );
    };

    if (allowance < calculateBribeAmount) {
      dispatchTxAction(
        dispatch,
        approveGameCashAction({
          account,
          spender: config.jailAddress,
          cashAmount: 10 ** 18,
        }),
        () => {
          buyOut();
        },
        CONFIRMATION_DELAY
      );
    } else {
      buyOut();
    }
  };

  useEffect(() => {
    dispatch(getJailedUntilAction(account));
  }, [account, dispatch]);

  const isInJail = jailedUntil - currentTime > 0;

  return (
    <Box className={classes.body}>
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Box component="img" src="/assets/imgs/injail/siren.png" />
          <Typography
            fontFamily={"Philosopher"}
            color={"white"}
            textAlign={"center"}
            fontSize={{ xs: 24, sm: 36 }}
            fontWeight={700}
          >
            {isInJail ? "You are behind bars!" : "You are out of jail!"}
          </Typography>

          <Typography
            fontFamily={"Philosopher"}
            color={"rgba(255, 255, 255, 0.4)"}
            textAlign={"center"}
            fontSize={14}
            fontWeight={700}
          >
            A rookie mobster thought he was untouchable-until betrayal, a
            wiretap, and a double-cross left him penniless, imprisoned, and
            forgotten. Crime pays, but never for long
          </Typography>
        </Box>

        <Box className={classes.content}>
          <Box
            component={"img"}
            src={
              isInJail
                ? "/assets/imgs/injail/in_jail.jpg"
                : "/assets/imgs/injail/out_jail.png"
            }
          />

          <Box className={classes.contentDetail}>
            <Box className={classes.contentDetailItem}>
              <Typography
                color={"white"}
                fontFamily={"Philosopher"}
                fontWeight={900}
              >
                Crime committed:
              </Typography>
              <Typography
                color={"rgba(255, 255, 255, 0.4)"}
                fontFamily={"Philosopher"}
                fontWeight={900}
              >
                {CrimeFailedReason[myCrimeType]}
              </Typography>
            </Box>

            <Box className={classes.contentDetailItem}>
              <Typography
                color={"white"}
                fontFamily={"Philosopher"}
                fontWeight={900}
              >
                Remaining sentence:
              </Typography>
              <Typography
                color={"rgba(255, 255, 255, 0.4)"}
                fontFamily={"Philosopher"}
                fontWeight={900}
              >
                {jailedUntil - currentTime > 0
                  ? convertDateTime(jailedUntil - currentTime)
                  : "You are out of jail."}
              </Typography>
            </Box>

            <Box className={classes.contentDetailItem}>
              <Typography
                color={"white"}
                fontFamily={"Philosopher"}
                fontWeight={900}
              >
                Bribe out:
              </Typography>
              <Button
                className={classes.bribeOutButton}
                startIcon={
                  isInJail ? (
                    <Box
                      component={"img"}
                      src="/assets/imgs/layout/currency.png"
                    ></Box>
                  ) : null
                }
                onClick={handleBuyOut}
                disabled={jailedUntil - currentTime <= 0}
              >
                {jailedUntil - currentTime > 0
                  ? toUSDFormat(Math.floor(calculateBribeAmount))
                  : "You are free now."}
              </Button>
            </Box>
          </Box>
        </Box>

        <Typography
          fontFamily={"Philosopher"}
          color={"rgba(255, 255, 255, 0.4)"}
          textAlign={"center"}
          fontSize={14}
          fontWeight={700}
          marginTop={4}
        >
          After a heist gone wrong and his closest friend gunned down, the
          mobster stared at the bloodstained cash, wondering if next time was
          worth it.
        </Typography>
      </Box>
    </Box>
  );
};

export default InJailPage;
